import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Download, Loader2, Share2 } from 'lucide-react';
import { getNote, updateNote } from './services/notes.service.ts';
import type { Note } from './types/note.types.ts';

const NoteEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      const data = await getNote(id);
      if (!data) {
        navigate('/products/notes', { replace: true });
        return;
      }
      setNote(data);
      setLoading(false);
    })();
  }, [id, navigate]);

  const persist = useCallback(async (patch: Partial<Pick<Note, 'title' | 'content'>>) => {
    if (!note) return;
    setSaving(true);
    try {
      const updated = await updateNote(note.id, patch);
      if (updated) setNote(updated);
    } finally {
      setSaving(false);
    }
  }, [note]);

  const scheduleSave = useCallback((patch: Partial<Pick<Note, 'title' | 'content'>>) => {
    if (!note) return;
    setNote({ ...note, ...patch });
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void persist(patch);
    }, 600);
  }, [note, persist]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleTitleBlur = (title: string) => {
    if (!note || title.trim() === note.title) return;
    void persist({ title: title.trim() || 'Adsız not' });
  };

  const handleContentChange = (content: string) => {
    scheduleSave({ content });
  };

  const handleExport = () => {
    if (!note) return;
    const blob = new Blob([`${note.title}\n\n${note.content}`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${note.title.replace(/\s+/g, '_')}.txt`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/products/notes/${id}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const wordCount = note?.content.trim()
    ? note.content.trim().split(/\s+/).length
    : 0;

  if (loading || !note) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-300 gap-3">
        <Loader2 className="w-5 h-5 animate-spin" />
        Not yükleniyor...
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-7.5rem)] -mt-2 lg:-mt-2">
      <div className="flex flex-col flex-1 min-h-0 bg-slate-800/50 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-4 pr-6 py-2 border-b border-white/10 bg-slate-900/50">
          <Link
            to="/products/notes"
            className="p-1 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition shrink-0"
            title="Geri"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>

          <input
            key={note.id}
            defaultValue={note.title}
            onBlur={(e) => handleTitleBlur(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-white font-semibold text-sm focus:outline-none border-b border-transparent focus:border-purple-500 truncate"
            aria-label="Not başlığı"
          />

          <span className="text-xs text-gray-500 hidden sm:inline shrink-0">
            {wordCount} kelime
          </span>

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {saving && <span className="text-xs text-gray-400 hidden sm:inline">Kaydediliyor...</span>}
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs sm:text-sm"
            >
              {copied ? <Copy className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{copied ? 'Kopyalandı' : 'Paylaş'}</span>
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm"
            >
              <Download className="w-3.5 h-3.5" />
              TXT
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3 pl-4 pr-6 pb-4">
          <textarea
            value={note.content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder="Notunuzu buraya yazın..."
            className="w-full h-full min-h-[420px] resize-none bg-amber-50/95 text-gray-800 rounded-lg border border-white/10 p-5 text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-500/40 placeholder:text-gray-400"
            aria-label="Not içeriği"
          />
        </div>
      </div>
    </div>
  );
};

export default NoteEditorPage;
